import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Dialog, DialogTitle, DialogContent, Alert, Snackbar, CircularProgress } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { getLiveAdverts, setLiveAdvertSlot, getAdvertLibrary } from '../../services/api';

const PAGES = [
  { key: 'calendar', label: 'Calendar' },
  { key: 'camera', label: 'Image' },
  { key: 'barcode', label: 'Barcode' }
];

const LiveAdverts = () => {
  const { groupId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [picker, setPicker] = useState(null);
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLiveAdverts(groupId);
      if (res.success) setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load live adverts');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const openPicker = async (page, position) => {
    setPicker({ page, position });
    setLibraryLoading(true);
    try {
      const res = await getAdvertLibrary(groupId);
      if (res.success) setLibraryItems(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load advert library');
    } finally {
      setLibraryLoading(false);
    }
  };

  const choose = async (libraryItemId) => {
    if (saving) return;
    const { page, position } = picker;
    setSaving(true);
    try {
      const res = await setLiveAdvertSlot(groupId, { page, position, libraryItemId });
      setData(prev => ({
        ...prev,
        [page]: prev[page].map(slot => slot.position === position ? { ...slot, ...res.data } : slot)
      }));
      setPicker(null);
      setSuccess('Advert updated');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update advert');
    } finally {
      setSaving(false);
    }
  };

  const clearSlot = () => choose(null);

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Live Adverts
      </Typography>

      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>Loading...</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 4 }}>
          {PAGES.map(({ key, label }) => (
            <Box key={key}>
              <Typography variant="h6" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
                {label.toUpperCase()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {[1, 2, 3].map((position) => {
                  const slot = data?.[key]?.[position - 1];
                  if (!slot) return <Box key={position} sx={{ width: 120 }} />;
                  return (
                    <Box key={position} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        onClick={slot.editable ? () => openPicker(key, slot.position) : undefined}
                        sx={{
                          position: 'relative',
                          width: 120, height: 160, border: '2px solid #1976d2',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: slot.editable ? 'pointer' : 'default',
                          overflow: 'hidden', textAlign: 'center', p: 1,
                          bgcolor: '#fafafa'
                        }}
                      >
                        {slot.image_url ? (
                          <Box component="img" src={slot.image_url} alt="" sx={{ maxWidth: '100%', maxHeight: '100%' }} />
                        ) : slot.editable ? (
                          <Typography variant="body2" fontWeight={600}>CLICK TO SELECT</Typography>
                        ) : null}
                        {!slot.editable && (
                          <Box sx={{
                            position: 'absolute', inset: 0,
                            bgcolor: 'rgba(120,120,120,0.55)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <LockIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 28 }} />
                          </Box>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ minHeight: 20 }}>
                        {!slot.editable ? 'SYSTEM AD' : ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={!!picker} onClose={() => !saving && setPicker(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Choose an advert</DialogTitle>
        <DialogContent sx={{ position: 'relative' }}>
          {saving && (
            <Box sx={{
              position: 'absolute', inset: 0, zIndex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.7)'
            }}>
              <CircularProgress size={32} />
            </Box>
          )}
          {libraryLoading ? (
            <Typography variant="body2" color="text.secondary">Loading...</Typography>
          ) : libraryItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No adverts in the library yet — add some on the Advert Library page first.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, py: 1 }}>
              <Box
                onClick={saving ? undefined : clearSlot}
                sx={{
                  width: 100, height: 100, border: '1px dashed #999',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: saving ? 'default' : 'pointer', textAlign: 'center', p: 1
                }}
              >
                <Typography variant="caption" color="text.secondary">Clear</Typography>
              </Box>
              {libraryItems.map((item) => (
                <Box
                  key={item.id}
                  onClick={saving ? undefined : () => choose(item.id)}
                  sx={{
                    width: 100, height: 100, border: '1px solid #ddd',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: saving ? 'default' : 'pointer', overflow: 'hidden', p: 1
                  }}
                >
                  {item.image_url ? (
                    <Box component="img" src={item.image_url} alt="" sx={{ maxWidth: '100%', maxHeight: '100%' }} />
                  ) : (
                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{item.link}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={5000}
        onClose={() => { setSuccess(''); setError(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSuccess(''); setError(''); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LiveAdverts;
